//
// # AgentRunTreeSchema — shared run-tree / snapshot types
//
// Pure-types package for the point-in-time run-tree / snapshot shapes the eval
// trajectory judge and dashboard read to inspect HOW an agent tree ran. No logic
// here, only the types plus their typia validators; consumers depend on the
// run-tree SHAPE without depending on the `Agent` implementation package.
//

import typia from "typia";
import { validate as v } from "CoreUtils";

// "running": a turn is executing. "idle": between turns but awaiting active
// work (a running child or an async job). "sleeping": alive but the only
// outstanding work is a deferred timer (a `delay`) — nothing is actively
// computing, so the UI shouldn't show it as working. "done"/"stopped": terminal.
export type AgentStatus = "running" | "idle" | "sleeping" | "done" | "stopped";

export interface AgentNodeSnapshot {
  agentId: string;
  parentId: string;
  name: string;
  status: AgentStatus;
  turnCount: number;
  // LM-written label given by the spawner, describing what this instance is for
  // (the conversation root has a fixed sentinel). Read by `list-agents` so a
  // parent can reuse a suitable existing child instead of spawning a duplicate.
  // Optional for back-compat: run-tree records persisted before this field
  // existed still validate on read (live snapshots always set it).
  description?: string;
  // What this agent is bound to (e.g. a design specialist's design), fixed at
  // spawn. Surfaced by `list-agents`. Optional: undefined when unbound, and for
  // back-compat with older persisted records.
  scopeLabel?: string;
  // Name of the most recent tool this agent invoked, or undefined before it has
  // called any. Surfaced in the Agents pane as a lightweight "what is it doing
  // right now" signal. Optional: undefined for agents that haven't called a
  // tool, and for back-compat with older persisted records.
  lastTool?: string;
}
export const AgentNodeSnapshot = {
  validator: new v.Validator(typia.createValidate<AgentNodeSnapshot>()),
};

export interface AgentTreeNode extends AgentNodeSnapshot {
  children: AgentTreeNode[];
}
export const AgentTreeNode = {
  validator: new v.Validator(typia.createValidate<AgentTreeNode>()),
};
